import { assertMover, isPassable } from './clearance.js';
import { cellAt } from './grid.js';
import { createFlowField } from './pathfinding.js';
import { createRng } from './rng.js';
/**
 * The way down: where a floor's stairs are, and what the floor below it is.
 *
 * ## Where the stairs go
 *
 * `docs/GAME-DESIGN.md`: *"Entrance sits against a map edge; the stairs down are
 * placed at maximum flow-field distance from it. The flow field already computes
 * that distance, so it costs almost nothing — and it guarantees every floor is a
 * real traversal rather than three steps to the exit."*
 *
 * **Flow distance, never Euclidean.** The two agree on an open floor and
 * disagree on exactly the floors the rule exists for: a cell on the other side
 * of a wall is a stone's throw away and a long walk away, and it is the walk the
 * design is buying. `createFlowField` already solves "how far is every cell from
 * here" for the whole map in one Dijkstra sweep, under the movement rule itself
 * — diagonals needing both flanking cells, and the mover's own erosion — so the
 * measure and the walk cannot disagree.
 *
 * **A disc, not a point.** The field is solved *for the body that has to get
 * there*, so a cell too tight for it is never expanded and can never be chosen.
 * Stairs a party's fattest member cannot path to are a floor that cannot be
 * finished, and no amount of checking afterwards fixes that — the erosion has to
 * happen before the maximum is taken, not after.
 *
 * **Ties break by reading order, and the tie is common.** "The farthest cell" is
 * frequently a whole ridge of cells at the same distance: two arms of a cave of
 * equal length, or the far wall of a room. The scan runs row-major over the
 * field's own `distances` array and a candidate must be *strictly* farther to
 * displace the one held, so the winner is the lowest row-major index among the
 * farthest — the same last-resort ordering `nearestReachable` and the dungeon's
 * region labelling already use. Two properties make that safe to rely on: the
 * array is indexed by cell rather than by the order the search happened to
 * settle cells in, so nothing about the search can leak into the answer; and the
 * distances themselves are the identical doubles on both sides, because both
 * sides run this same sweep with the same fixed neighbour order and the same
 * insertion-order heap. A tie settled by iteration order — a `Set`, a
 * `flatMap`, the settle order — is the desync that shows up on one seed in
 * fifty, on the client of one player, and nowhere in a test that only ever asks
 * one process.
 *
 * ## Why this is not part of `generateDungeon`
 *
 * Because the stairs depend on the *body*, and generation does not know about
 * bodies. Core states a movement speed but no body size — `PLAYER_RADIUS` lives
 * in `apps/game` (`TODO.md` #71) — so a `Dungeon` carrying a stairs cell would
 * either have to invent a radius or place stairs for a point, which is precisely
 * the placement that can strand a wide body. Keeping it a separate call also
 * keeps the cost honest: this is one full-map Dijkstra sweep, paid once per
 * floor at generation time, exactly like `nearestReachable` pays one per click.
 *
 * **Both sides must place the stairs with the same radius**, since the answer is
 * a function of it. That is a real hazard the moment the client regrows a floor
 * locally, and the cheap answer is the one the grid hash already uses: put the
 * cell on the wire and let the client check its own against it.
 *
 * ## What a floor *is*, across a descent
 *
 * A floor is a seed (`ARCHITECTURE.md`: *"Levels cost ~40 bytes on the wire"*),
 * so descending is a new pair of numbers — a seed and a depth — and both sides
 * regrow the identical cave from them.
 *
 * **The next seed is derived from the one being left, not drawn from the run's
 * random stream.** `descend` takes a floor and returns a floor; it holds no
 * cursor, reads no stream, and touches nothing else in the run. That is the
 * whole argument, and it is worth spelling out because the alternative looks
 * cheaper:
 *
 *   - **Drawn from the run's stream, a floor's identity depends on everything
 *     that has already been rolled.** Every crit, every drop and every affix
 *     moves the cursor, so the cave below would depend on how the fight above
 *     went. The client cannot replay those draws — monsters are server-spawned
 *     and loot is rolled server-side — so it could not derive the next floor at
 *     all, and the *server* could not either after a restart without replaying
 *     the whole run.
 *   - **Drawn from a stream dedicated to floors, it still depends on how many
 *     floors have been made.** Re-entering a floor — a town portal back down,
 *     a rejoin, a corpse run — would either produce a different cave or need a
 *     list of every seed ever drawn kept beside the run.
 *   - **Derived, a run is one seed end to end.** Floor *n* is a pure function of
 *     the run's seed applied *n* − 1 times, so any floor is re-derivable from
 *     the run's own identity, in any order, with no history at all. A floor can
 *     be re-entered and comes back identical, which is what a town and a portal
 *     need to exist. And a room holding `{seed, depth}` can compute the floor
 *     below it without a run object existing yet, which is the shape `TODO.md`
 *     #171 asks for — *"the room the stairs led out of"*.
 *
 * The mixing is `rng.ts`'s own stream rather than a hash invented here: the next
 * seed is the first draw of a stream seeded by the current one. It is drawn over
 * the rng's whole 32-bit state space because `restoreRng` folds a seed with
 * `| 0` — a derivation that moved only the bits above that would name a
 * different floor and grow the identical cave.
 *
 * ## What is declared and inert
 *
 * **How a floor gets harder as you descend.** `ilvl = floor depth` and a
 * monster's level is its floor's depth; that is the whole of what depth
 * currently moves, and it is a rule this project decided rather than a curve.
 * Everything else — monster life, damage, density, cave shape — would need a
 * number authored, and inventing one is work `TODO.md` #15 has deliberately not
 * done (#270 for the monster table, #44 for the cave). So this module states the
 * *shape* — the next depth from a depth, and the bound that it never decreases —
 * and says the rest is missing rather than guessing at it.
 */
/**
 * The floor a descent starts on, and the shallowest depth that is a depth.
 *
 * Not a number chosen here: `loot.ts`'s `itemLevelForDepth` already refuses
 * anything below one, because `ilvl = depth` and there is no item level zero.
 * This is that same floor of the range, named so a run has somewhere to start.
 */
export const FIRST_DEPTH = 1;
/**
 * The note this module owes the reader in place of a difficulty curve.
 *
 * Declared as a value rather than left in a comment for the same reason
 * `elites.ts` declares its inert affixes with an `unimplemented` string: a gap
 * that only exists in prose is a gap the next caller does not find.
 */
export const DEPTH_SCALING_UNIMPLEMENTED = 'Depth moves the item level (ilvl = depth) and the level monsters are born at, and nothing else. ' +
    'Monster life, damage, density, aggro and cave shape do not scale with depth, so floor 12 is floor 1 ' +
    'with better loot. Every candidate curve is a balance number this project has not authored ' +
    '(TODO.md #15), and the honest fix is the monster table itself (#270) and per-depth cave tuning (#44) ' +
    'rather than a multiplier invented in core.';
/** The rng's whole state space: `restoreRng` folds any seed into an int32 cursor. */
const SEED_SPACE = 0x1_0000_0000;
const MIN_SEED = -SEED_SPACE / 2;
const MAX_SEED_EXCLUSIVE = SEED_SPACE / 2;
const assertDepth = (depth) => {
    // Phrased as a negated comparison so NaN — a depth that was never a number —
    // is rejected along with the out-of-range values.
    if (!(Number.isSafeInteger(depth) && depth >= FIRST_DEPTH)) {
        throw new RangeError(`A floor depth must be a whole number of at least ${FIRST_DEPTH}, received ${depth}`);
    }
};
const assertSeed = (seed) => {
    if (!Number.isSafeInteger(seed)) {
        throw new RangeError(`A floor seed must be a safe integer, received ${seed}`);
    }
};
/**
 * The floor below this one.
 *
 * One step, always, and always deeper: the depth of a floor is the item level of
 * everything it drops, so a descent that could stand still or double back would
 * make "descending is the progression lever" untrue. That is the only bound core
 * can state without authoring a curve — see `DEPTH_SCALING_UNIMPLEMENTED`.
 *
 * There is no maximum. A ceiling would be a content decision (how long a run
 * is), and inventing one here would silently cap the item level of the whole
 * game.
 */
export const nextDepth = (depth) => {
    assertDepth(depth);
    return depth + 1;
};
/**
 * The seed of the floor below the one this seed names.
 *
 * The first draw of a stream seeded by the current floor — deterministic,
 * dependent on nothing but its argument, and reproducible on a client that has
 * seen none of the run so far.
 */
export const nextFloorSeed = (seed) => {
    assertSeed(seed);
    return createRng(seed).int(MIN_SEED, MAX_SEED_EXCLUSIVE);
};
/** Where a run begins: the seed it was named by, on the first floor. */
export const firstFloor = (runSeed) => {
    assertSeed(runSeed);
    return { seed: runSeed, depth: FIRST_DEPTH };
};
/**
 * Takes the stairs: the next floor's identity, from this one's.
 *
 * A pure function of the floor being left, which is the whole seed decision (see
 * the module note). Nothing is rolled, so two clients that have played the same
 * run differently still arrive on the same cave.
 */
export const descend = ({ seed, depth }) => ({
    seed: nextFloorSeed(seed),
    depth: nextDepth(depth),
});
/**
 * Where this floor's stairs down go: the cell farthest from the entrance *to
 * walk to*, that this body can both stand on and reach.
 *
 * `null` is the one refusal, and it is never about the map: it means the mover
 * cannot stand on the entrance itself, so there is no floor here for a body this
 * wide at all. An ordinary returned outcome rather than a throw — the caller
 * decides whether that means a narrower body or another seed. A mover built for
 * a different map, or a nonsense radius, *is* a broken program and throws, the
 * same line `findPath` draws.
 *
 * On a floor whose entrance is the only cell anything can reach, the stairs are
 * the entrance. That is the honest reading of "the farthest reachable cell" when
 * there is exactly one, and the alternative — no stairs at all — is a floor with
 * no way out; a one-cell cave is not a traversal whatever is returned.
 */
export const placeStairs = (grid, entrance, mover) => {
    assertMover(grid, mover);
    if (!isPassable(grid, entrance, mover))
        return null;
    // Rooted at the entrance and eroded for this body, so `distances` is the cost
    // of *walking* to each cell and `Infinity` marks everything this body cannot
    // reach or cannot fit in — rock included, since a cell that is never expanded
    // is never relaxed.
    const field = createFlowField(grid, entrance, mover);
    // Row-major over the field's own array, never over the order cells were
    // settled in, and strictly farther to displace — so of two equally distant
    // cells the earlier one holds, and the tie is broken by the map rather than by
    // the search. The entrance is always a candidate at distance zero, so the loop
    // cannot come out empty.
    let best = null;
    let bestDistance = Number.NEGATIVE_INFINITY;
    for (let index = 0; index < field.distances.length; index += 1) {
        const travel = field.distances[index] ?? Number.POSITIVE_INFINITY;
        if (travel === Number.POSITIVE_INFINITY)
            continue;
        if (travel <= bestDistance)
            continue;
        best = cellAt(grid, index);
        bestDistance = travel;
    }
    return best;
};
//# sourceMappingURL=descent.js.map